import { ChatComposer } from '@repo/ui'
import { LoaderCircle } from 'lucide-react'
import { useState } from 'react'
import { useElapsed } from '../Progress'
import type { ReactNode } from 'react'

// Refine instruction input for a canvas section, built on the shared chat composer so trimmed-only
// submission, Enter-to-send, and the busy lock/stop swap all come from one place (same component
// ProposalSectionChat uses). The caller owns stream state and passes it down; nothing here talks
// to the network. Unmounted until the canvas panel work wires it in.
// While busy, ChatComposer locks the textarea and greys the send button, and without an
// onStop that is the whole of the feedback: indistinguishable from a composer that is simply
// broken. The presentation refines have nothing else to say it either, because their POST
// returns only when the work is finished, so there is no streamed draft appearing above to
// stand in for progress the way the proposal section's does.
//
// No bar. A bar is a claim about position and a refine has none to make: it is one request
// that returns when it returns. The first version borrowed ConversationProgress's
// indeterminate branch, a fixed one-third-width fill with animate-pulse, which does not
// travel and so reads as a determinate bar stalled at 33%. The spinner says work is
// happening and the clock says it is still alive, which is all that is actually known.
//
// The clock is shared with the chat rather than copied, so the two surfaces cannot disagree
// about what 1:05 means.
function RefineProgress({ label }: { label: string }) {
  const [startedAtTsMs] = useState(() => Date.now())
  const elapsed = useElapsed(startedAtTsMs)
  return (
    // role="status" rather than an aria-label on a bare div, which has no role to hang a name
    // on and is not reliably announced. It announces once, when the refine starts, because the
    // label is static.
    //
    // The clock is aria-hidden for the same reason: it lives inside the live region and
    // changes every second, so a screen reader would read the whole line once a second. Sighted
    // readers need it to tell slow from stuck; a screen reader was already told work began.
    <div data-testid="refine-progress" role="status" className="flex items-center gap-2 text-xs">
      <LoaderCircle
        className="size-3.5 shrink-0 animate-spin text-muted-foreground motion-reduce:animate-none"
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1 truncate text-muted-foreground">{label}</span>
      {elapsed !== undefined && (
        <span aria-hidden="true" className="shrink-0 text-muted-foreground tabular-nums">
          {elapsed}
        </span>
      )}
    </div>
  )
}

export function RefineComposer({
  placeholder,
  busy,
  disabled = false,
  error,
  label = 'Refining',
  onSubmit,
  onStop
}: {
  placeholder: string
  busy: boolean
  disabled?: boolean
  error: string | null
  // What the progress line says while busy. Defaulted rather than required so the three
  // existing call sites keep working, but every one of them passes a subject: eight slide
  // composers on one canvas each announcing a bare "Refining" would name nothing.
  label?: string
  onSubmit: (instruction: string) => void
  onStop?: () => void
}): ReactNode {
  return (
    <div className="flex flex-col gap-1">
      {error !== null && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      {/* Keyed on the label so a second refine of a different subject restarts the clock
          rather than continuing the first one's count. */}
      {busy && <RefineProgress key={label} label={label} />}
      <ChatComposer
        onSend={onSubmit}
        isStreaming={busy}
        isDisabled={disabled}
        placeholder={placeholder}
        // Only forward onStop when given; ChatComposer shows the stop affordance iff it is present.
        {...(onStop !== undefined && { onStop })}
      />
    </div>
  )
}
